import { useState, useEffect } from 'preact/hooks';
import { apiGet } from '../lib/api';

interface AuditEntry {
  id: string;
  user_chat_id: string;
  action: string;
  target: string | null;
  result: string;
  detail: Record<string, unknown> | null;
  created_at: string;
}

interface AuditLogProps {
  /** Limit entries to display */
  limit?: number;
  /** Show filter controls */
  showFilter?: boolean;
}

const ACTION_LABELS: Record<string, string> = {
  login: '🔑 Login',
  restart: '🔄 Restart',
  ssh_session: '💻 SSH',
  config_change: '⚙️ Config',
  workflow_change: '⚡ Workflow',
};

const RESULT_COLORS: Record<string, string> = {
  success: 'audit-success',
  failure: 'audit-failure',
  blocked: 'audit-blocked',
};

export function AuditLog({ limit = 20, showFilter = true }: AuditLogProps) {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    loadEntries();
  }, [filter]);

  async function loadEntries() {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: String(limit) });
      if (filter) params.set('action', filter);
      const data = await apiGet<AuditEntry[]>(`/audit?${params}`);
      setEntries(data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }

  if (loading) return <p class="empty-hint">Audit-Log laden...</p>;

  return (
    <div class="audit-log">
      {showFilter && (
        <div class="audit-filter">
          <select
            class="form-select form-select-sm"
            value={filter}
            onChange={(e) => setFilter((e.target as HTMLSelectElement).value)}
          >
            <option value="">Alle Aktionen</option>
            <option value="login">Login</option>
            <option value="restart">Restart</option>
            <option value="ssh_session">SSH</option>
            <option value="config_change">Config</option>
            <option value="workflow_change">Workflow</option>
          </select>
        </div>
      )}

      {entries.length === 0 ? (
        <p class="empty-hint">Keine Einträge.</p>
      ) : (
        <ul class="audit-list">
          {entries.map((entry) => (
            <li key={entry.id} class={`audit-entry ${RESULT_COLORS[entry.result] ?? ''}`}>
              <div class="audit-entry-header">
                <span class="audit-action">
                  {ACTION_LABELS[entry.action] ?? entry.action}
                </span>
                <span class="audit-time">
                  {new Date(entry.created_at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}
                </span>
              </div>
              <div class="audit-entry-body">
                {entry.target && <span class="audit-target">{entry.target}</span>}
                <span class={`audit-result audit-result-${entry.result}`}>{entry.result}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
